/**
 * What a valid agent is, in one place.
 *
 * The values are the shape `buildRequestTemplate` and `parseSavedAgent`
 * already read and write, so the wire mapping is untouched by the form. Every
 * rule here mirrors one the backend enforces, by way of the limit constants
 * beside the old editor's predicates — the numbers are imported rather than
 * restated so there is one place they can drift from the server.
 *
 * Each rule sits on the smallest thing it is about, which is what puts every
 * error beside its own control: a bad label in the second goal's third row
 * lands on `goals.1.label_vocabulary.2.label`. Only the rule that genuinely
 * crosses sections — an agent with neither a goal nor a kickoff message has
 * nothing to do — is left at the root.
 */

import { z } from "zod";

import { type TemplateVariable } from "@/shared/domain/agents";

import {
  MAX_EVENT_FOCUS_PROMPT_CHARS,
  MAX_EVENT_VOCABULARY,
  MAX_SUMMARY_FORMAT_CHARS,
  MAX_TAG_VOCABULARY,
  MAX_TRIAGE_DESCRIPTION_CHARS,
  MAX_TRIAGE_LABELS,
  MAX_VOCABULARY_DESCRIPTION_CHARS,
  MAX_VOCABULARY_KEY_CHARS,
  TRIAGE_LABEL_PATTERN,
} from "../Agents/goals/validation";
import { MAX_AVAILABLE_SKILLS } from "../Agents/skills/validation";

/**
 * The most goals one agent may carry: the cap the agent handler applies to a
 * single turn.
 */
export const MAX_GOALS = 5;

/**
 * Reports every row after the first that repeats `key`, on that row's own
 * cell, so the duplicate is flagged where it was typed rather than on the list.
 */
function uniqueBy<Schema extends z.ZodType>(
  key: string,
  message: (firstRow: number) => string,
  schema: Schema,
): Schema {
  return schema.superRefine((rows, ctx) => {
    if (!Array.isArray(rows)) {
      return;
    }
    const seenAt = new Map<unknown, number>();
    rows.forEach((row: Record<string, unknown>, index: number) => {
      const value = row[key];
      const firstRow = seenAt.get(value);
      if (firstRow === undefined) {
        seenAt.set(value, index);
        return;
      }
      ctx.addIssue({
        code: "custom",
        path: [index, key],
        message: message(firstRow),
      });
    });
  });
}

/**
 * The two halves of a create-events vocabulary row: the key the agent writes
 * onto what it creates, and when it applies. Both vocabularies carry the same
 * rules under different key names.
 */
const vocabularyKey = z
  .string()
  .refine((key) => key.trim() !== "", "Required.")
  .max(
    MAX_VOCABULARY_KEY_CHARS,
    `Must be at most ${MAX_VOCABULARY_KEY_CHARS} characters.`,
  );

const vocabularyDescription = z
  .string()
  .refine(
    (description) => description.trim() !== "",
    "Describe when this applies.",
  )
  .max(
    MAX_VOCABULARY_DESCRIPTION_CHARS,
    `Description must be at most ${MAX_VOCABULARY_DESCRIPTION_CHARS} characters.`,
  );

const datasetSummaryGoal = z.object({
  goal_type: z.literal("dataset_summary"),
  /** Empty means "use the handler's own format", so only overflow is a problem. */
  summary_format_spec_prompt: z
    .string()
    .max(
      MAX_SUMMARY_FORMAT_CHARS,
      `Summary format hint must be at most ${MAX_SUMMARY_FORMAT_CHARS} characters.`,
    ),
});

const datasetTriageGoal = z.object({
  goal_type: z.literal("dataset_triage"),
  label_vocabulary: uniqueBy(
    "label",
    (firstRow) => `Duplicate of label at row ${firstRow + 1}.`,
    z
      .array(
        z.object({
          label: z
            .string()
            .refine((label) => label.trim() !== "", "Label is required.")
            .regex(
              TRIAGE_LABEL_PATTERN,
              "Use only letters, digits, underscores, or hyphens (no spaces).",
            ),
          description: z
            .string()
            .refine(
              (description) => description.trim() !== "",
              "Describe what this label signifies.",
            )
            .max(
              MAX_TRIAGE_DESCRIPTION_CHARS,
              `Description must be at most ${MAX_TRIAGE_DESCRIPTION_CHARS} characters.`,
            ),
        }),
      )
      .min(1, "Add at least one label.")
      .max(MAX_TRIAGE_LABELS, `At most ${MAX_TRIAGE_LABELS} labels per goal.`),
  ),
});

const createEventsGoal = z.object({
  goal_type: z.literal("create_events"),
  event_vocabulary: uniqueBy(
    "name",
    (firstRow) => `Duplicate of row ${firstRow + 1}.`,
    z
      .array(
        z.object({ name: vocabularyKey, description: vocabularyDescription }),
      )
      .min(1, "Add at least one event type.")
      .max(MAX_EVENT_VOCABULARY, `At most ${MAX_EVENT_VOCABULARY} entries.`),
  ),
  /** Tags are optional: the event vocabulary alone steers the agent. */
  tag_vocabulary: uniqueBy(
    "tag",
    (firstRow) => `Duplicate of row ${firstRow + 1}.`,
    z
      .array(
        z.object({ tag: vocabularyKey, description: vocabularyDescription }),
      )
      .max(MAX_TAG_VOCABULARY, `At most ${MAX_TAG_VOCABULARY} entries.`),
  ),
  collection_enabled: z.boolean(),
  event_focus_prompt: z
    .string()
    .max(
      MAX_EVENT_FOCUS_PROMPT_CHARS,
      `Event focus must be at most ${MAX_EVENT_FOCUS_PROMPT_CHARS} characters.`,
    ),
});

const goalSchema = z.discriminatedUnion("goal_type", [
  datasetSummaryGoal,
  datasetTriageGoal,
  createEventsGoal,
]);

export interface AgentFormSchemaOptions {
  /**
   * The skills the org offers an agent right now. A row naming anything else
   * is a skill that was deleted or made private since it was attached.
   *
   * Absent while the list is still on its way, and then nothing is judged:
   * an unsettled list would read as "the org offers none" and flag every
   * attached skill the moment an edit form opened.
   */
  eligibleSkillIds?: ReadonlySet<string>;
}

/**
 * The form's rules, given what the surrounding page knows. Everything except
 * the skills on offer is fixed, so the factory exists for that one input.
 */
export function makeAgentFormSchema({
  eligibleSkillIds,
}: AgentFormSchemaOptions) {
  return z
    .object({
      name: z.string().trim().min(1, "Enter a name for the agent."),
      description: z.string(),
      /** Null means the org's default profile. */
      modelProfile: z.string().nullable(),
      kickoffMessage: z.string(),
      goals: z.array(goalSchema),
      skills: uniqueBy(
        "skill_id",
        (firstRow) => `Duplicate of the skill at row ${firstRow + 1}.`,
        z
          .array(
            z.object({
              skill_id: z.string(),
              /** Null tracks the latest version. */
              version: z.number().int().nullable(),
            }),
          )
          .max(
            MAX_AVAILABLE_SKILLS,
            `At most ${MAX_AVAILABLE_SKILLS} skills can be attached to an agent.`,
          ),
      ).superRefine((rows, ctx) => {
        if (eligibleSkillIds === undefined) {
          return;
        }
        rows.forEach((row, index) => {
          if (!eligibleSkillIds.has(row.skill_id)) {
            ctx.addIssue({
              code: "custom",
              path: [index, "skill_id"],
              message: "This skill is no longer available — remove it.",
            });
          }
        });
      }),
      /**
       * Described and defaulted per variable. The names are read out of the
       * text that mentions them, never typed here, so nothing is judged.
       */
      variableMeta: z.record(
        z.string(),
        z.object({ description: z.string(), default: z.string() }),
      ),
      /**
       * Saved goals the form cannot edit, written back unchanged. Never judged
       * here, but they count toward the goal cap and as a goal for the
       * kickoff-message rule.
       */
      passthroughGoals: z.array(
        z.object({
          savedIndex: z.number(),
          goal: z.record(z.string(), z.unknown()),
        }),
      ),
      /** Saved variable declarations written back unchanged. */
      passthroughVariables: z.array(z.custom<TemplateVariable>()),
    })
    .superRefine((values, ctx) => {
      const kept = values.passthroughGoals.length;
      const goalCount = values.goals.length + kept;
      // The server caps the whole list, so goals the form cannot edit take
      // up room too. Reported on the list, where "Add goal" sits.
      if (goalCount > MAX_GOALS) {
        ctx.addIssue({
          code: "custom",
          path: ["goals"],
          message:
            kept === 0
              ? `At most ${MAX_GOALS} goals per agent.`
              : `At most ${MAX_GOALS} goals per agent, including the ${kept} the editor cannot change.`,
        });
      }
      // An agent with no goal and nothing to say has no work to do. Reported on
      // the kickoff message because that is the field the person can fill in
      // without choosing a goal.
      if (goalCount === 0 && values.kickoffMessage.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["kickoffMessage"],
          message: "Add a goal above, or say here what the agent should do.",
        });
      }
    });
}

/** The rules that hold whatever the page has loaded. */
export const agentFormSchema = makeAgentFormSchema({});

export type AgentFormValues = z.input<typeof agentFormSchema>;
/** What the resolver hands `handleSubmit`: the name is already trimmed. */
export type AgentFormOutput = z.output<typeof agentFormSchema>;
export type GoalFormValues = AgentFormValues["goals"][number];
export type GoalKind = GoalFormValues["goal_type"];

/**
 * What a goal of each kind starts on. A factory rather than constants so no
 * two goals alias one vocabulary array.
 */
export function newGoalValues(kind: GoalKind): GoalFormValues {
  if (kind === "dataset_summary") {
    return { goal_type: kind, summary_format_spec_prompt: "" };
  }
  if (kind === "create_events") {
    return {
      goal_type: kind,
      event_vocabulary: [{ name: "", description: "" }],
      tag_vocabulary: [],
      collection_enabled: false,
      event_focus_prompt: "",
    };
  }
  return {
    goal_type: kind,
    label_vocabulary: [{ label: "", description: "" }],
  };
}
